import {
    autoUpdate,
    FloatingFocusManager,
    FloatingOverlay,
    FloatingPortal,
    size as floatingSize,
    offset,
    Placement,
    shift,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole,
    useTransitionStyles,
    useTypeahead
} from '@floating-ui/react';

import { faBars, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonProps } from '@ui/Button';
import Spinner from '@ui/Spinner';
import {
    Children,
    cloneElement,
    forwardRef,
    isValidElement,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

export type DropdownPlacement = Placement;

export interface DropdownProps extends ButtonProps {
    isLoading?: boolean;
    label?: ReactNode;
    icon?: ReactNode;
    value?: string;
    slim?: boolean;
    containerClassName?: string;
    /**
     * The type size of the rows, independent of the trigger's `size`.
     *
     * The two used to be one prop, which meant a 24px trigger in a dense toolbar
     * could only be had together with rows set in the same 12px — and since the
     * `xs` row variant did not exist, what those menus actually rendered was the
     * inherited 16px body size. Separating them lets a small trigger keep the
     * rows every other menu in the app has, and lets a toolbar ask for smaller
     * rows without shrinking its own button.
     */
    itemSize?: ButtonProps['size'];
    placement?: DropdownPlacement;
    triggerElement?: ReactElement<any>;
    centerRelativeToButton?: boolean;
    /**
     * Keeps the menu open after a row is clicked.
     *
     * For menus where a click is a *toggle* rather than a choice — ticking three
     * weekdays in a filter — closing after the first tick means reopening the
     * menu for every further one. Defaults to closing, which is right for every
     * menu that answers a question once.
     */
    closeOnSelect?: boolean;
}

export interface DropdownRef {
    open: () => void;
    close: () => void;
}

const MENU_VIEWPORT_PADDING = 12;
const MENU_OFFSET = 8;

/**
 * Picks the side with more room, measured at the trigger instead of at the menu.
 * flip() can't do this here: size() has already constrained the menu, so it
 * "fits" into a 60px gap below the trigger, flip() sees no overflow and leaves
 * it there — a scrolling stub with 600px of unused space above it.
 */
const flipToRoomierSide = {
    name: 'flipToRoomierSide',
    fn({ placement, elements }: { placement: Placement; elements: { reference: any; floating: HTMLElement } }) {
        const [side, alignment] = placement.split('-');

        if (side !== 'top' && side !== 'bottom') {
            return {};
        }

        const reference = elements.reference.getBoundingClientRect();
        const roomBelow = window.innerHeight - reference.bottom - MENU_OFFSET - MENU_VIEWPORT_PADDING;
        const roomAbove = reference.top - MENU_OFFSET - MENU_VIEWPORT_PADDING;
        const room = side === 'bottom' ? roomBelow : roomAbove;
        const oppositeRoom = side === 'bottom' ? roomAbove : roomBelow;
        const wanted = elements.floating.scrollHeight;

        if (room >= wanted || oppositeRoom <= room) {
            return {};
        }

        const opposite = side === 'bottom' ? 'top' : 'bottom';

        return { reset: { placement: (alignment ? `${opposite}-${alignment}` : opposite) as Placement } };
    }
};

const DropdownMenu = forwardRef<DropdownRef, PropsWithChildren<DropdownProps>>(
    (
        {
            children,
            size = 'xs',
            itemSize,
            label = 'Optionen',
            slim,
            isLoading,
            className,
            containerClassName,
            triggerElement,
            icon,
            placement = 'bottom',
            centerRelativeToButton = false,
            closeOnSelect = true,
            ...props
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = useState(false);
        const [activeIndex, setActiveIndex] = useState<number | null>(null);

        useImperativeHandle(ref, () => ({
            close: () => setIsOpen(false),
            open: () => setIsOpen(true)
        }));

        const listItemsRef = useRef<Array<HTMLButtonElement | HTMLAnchorElement | null>>([]);
        const listContentRef = useRef(
            // @ts-ignore
            Children.map(children, (child) => (isValidElement(child) ? child.props.label : null)) as Array<
                string | null
            >
        );

        const { refs, floatingStyles, context } = useFloating({
            open: isOpen,
            onOpenChange: setIsOpen,
            middleware: [
                offset({ mainAxis: MENU_OFFSET }),
                flipToRoomierSide,
                shift({ padding: 10 }),
                floatingSize({
                    padding: MENU_VIEWPORT_PADDING,
                    apply({ availableHeight, elements }) {
                        elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
                    }
                })
            ],
            placement: placement,
            strategy: 'fixed',
            whileElementsMounted: autoUpdate
        });

        const role = useRole(context, { role: 'menu' });
        const dismiss = useDismiss(context, { ancestorScroll: true });
        const { isMounted, styles: transitionStyles } = useTransitionStyles(context, { duration: 150 });
        const listNavigation = useListNavigation(context, {
            listRef: listItemsRef,
            onNavigate: setActiveIndex,
            activeIndex
        });
        const typeahead = useTypeahead(context, {
            enabled: isOpen,
            listRef: listContentRef,
            onMatch: setActiveIndex,
            activeIndex
        });
        const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
            role,
            dismiss,
            listNavigation,
            typeahead
        ]);

        return (
            <>
                <div className={containerClassName ?? className}>
                    {triggerElement ? (
                        cloneElement(triggerElement, {
                            ref: refs.setReference,
                            ...getReferenceProps({
                                onClick: (e) => {
                                    setIsOpen(!isOpen);
                                    e.stopPropagation();
                                    e.preventDefault();
                                }
                            })
                        })
                    ) : (
                        <Button
                            disabled={isLoading}
                            className={className}
                            type="button"
                            size={size}
                            color="light"
                            ref={refs.setReference}
                            {...props}
                            {...getReferenceProps({
                                onClick: (e) => {
                                    setIsOpen(!isOpen);
                                    e.stopPropagation();
                                    e.preventDefault();
                                }
                            })}
                        >
                            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-between tw-gap-2 tw-whitespace-nowrap tw-leading-5">
                                {isLoading ? (
                                    <Spinner size="xs" color="currentColor" />
                                ) : slim ? (
                                    (icon ?? <FontAwesomeIcon icon={faBars} />)
                                ) : (
                                    <>
                                        <span className="tw-min-w-0 tw-flex-1 tw-truncate">{label}</span>
                                        {icon ?? <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />}
                                    </>
                                )}
                            </span>
                        </Button>
                    )}
                </div>
                {isOpen && isMounted && (
                    <FloatingPortal>
                        <FloatingOverlay />
                        <FloatingFocusManager
                            modal={false}
                            returnFocus={false}
                            context={context}
                            initialFocus={refs.floating}
                        >
                            <div
                                // Width capped for the same reason as the height,
                                // and the entries wrap rather than being cut: a
                                // menu is as wide as its longest label, and a
                                // form field called "Vorname/first name/prénom"
                                // is a perfectly ordinary label. Truncating would
                                // hide exactly the part that tells two similarly
                                // named fields apart.
                                className="tw-max-w-[min(32rem,calc(100vw-1.5rem))] tw-overflow-y-auto tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-2 tw-shadow-lg focus:tw-outline-none"
                                ref={refs.setFloating}
                                style={{ ...floatingStyles, ...transitionStyles, zIndex: 2 ** 31 - 100 }}
                                {...getFloatingProps()}
                            >
                                {Children.map(
                                    children,
                                    (child: any, index) =>
                                        isValidElement(child) &&
                                        cloneElement(
                                            child,
                                            getItemProps({
                                                tabIndex: activeIndex === index ? 0 : -1,
                                                ref(node: HTMLButtonElement | HTMLAnchorElement) {
                                                    listItemsRef.current[index] = node;
                                                },
                                                onClick(event) {
                                                    if (closeOnSelect) setIsOpen(false);
                                                    // call original onClick if present
                                                    (
                                                        child as ReactElement<any> & {
                                                            props: { onClick?: (e: any) => void };
                                                        }
                                                    ).props.onClick?.(event);
                                                    event.stopPropagation();
                                                },
                                                // rows follow the menu size as before; `xs` rendered 16px while it had no variant
                                                size: itemSize ?? (size === 'xs' ? 'lg' : size)
                                            } as any)
                                        )
                                )}
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </>
        );
    }
);

DropdownMenu.displayName = 'Dropdown';

export default DropdownMenu;
